
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

interface UserEditFormProps {
  user: any;
  onCancel: () => void;
  onSave: (userData: any) => void;
}

export const UserEditForm: React.FC<UserEditFormProps> = ({ 
  user, 
  onCancel, 
  onSave 
}) => {
  const [formData, setFormData] = useState({
    full_name: user.full_name || '',
    email: user.email || '',
    phone: user.phone || user.phone_number || '',
    country: user.country || '',
    verified: !!user.verified,
    blocked: !!user.blocked,
    type: user.type || 'user',
    professional_license: user.professional_license || '',
    practice_address: user.practice_address || '',
    about_me: user.about_me || '',
    specialties: user.specialties || [],
  });
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  
  const handleCheckboxChange = (field: string) => (checked: boolean) => {
    setFormData({
      ...formData,
      [field]: checked,
    });
  };

  const handleSpecialtiesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const specialtiesString = e.target.value;
    const specialtiesArray = specialtiesString.split(',').map(s => s.trim()).filter(Boolean);
    
    setFormData({
      ...formData,
      specialties: specialtiesArray,
    });
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="full_name" className="text-sm font-medium">
              Nom complet
            </label>
            <Input
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="phone" className="text-sm font-medium">
              Téléphone
            </label>
            <Input
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="country" className="text-sm font-medium">
              Pays
            </label>
            <Input
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="type" className="text-sm font-medium">
              Type d'utilisateur
            </label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md"
            >
              <option value="user">Utilisateur</option>
              <option value="professional">Professionnel</option>
            </select>
          </div>
          <div className="flex flex-col space-y-2 pt-5">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="verified"
                checked={formData.verified}
                onCheckedChange={handleCheckboxChange('verified')}
              />
              <label
                htmlFor="verified"
                className="text-sm font-medium leading-none"
              >
                Compte vérifié
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="blocked"
                checked={formData.blocked}
                onCheckedChange={handleCheckboxChange('blocked')}
              />
              <label
                htmlFor="blocked"
                className="text-sm font-medium leading-none"
              >
                Compte bloqué
              </label>
            </div>
          </div>
        </div>
        
        {formData.type === 'professional' && (
          <div className="space-y-4 border rounded-md p-4">
            <h3 className="text-sm font-medium">Informations professionnelles</h3>
            
            <div className="space-y-2">
              <label htmlFor="professional_license" className="text-sm font-medium">
                Numéro de licence
              </label>
              <Input
                id="professional_license"
                name="professional_license"
                value={formData.professional_license}
                onChange={handleChange}
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="practice_address" className="text-sm font-medium">
                Adresse du cabinet
              </label>
              <Input
                id="practice_address"
                name="practice_address"
                value={formData.practice_address}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="specialties" className="text-sm font-medium">
                Spécialités (séparées par des virgules)
              </label>
              <Input
                id="specialties"
                name="specialties"
                value={formData.specialties.join(', ')}
                onChange={handleSpecialtiesChange}
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="about_me" className="text-sm font-medium">
                À propos
              </label>
              <Textarea
                id="about_me"
                name="about_me"
                value={formData.about_me}
                onChange={handleChange}
                rows={3}
              />
            </div>
          </div>
        )}
      </div>
      
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit">
          Enregistrer
        </Button>
      </div>
    </form>
  );
};
